/**
 * @file chefOrderCreation.service.ts
 * @description Dedicated service for menu item discovery and direct kitchen order creation in TableGreet Chef.
 */

import { supabase } from '../../../config/supabase.config';

export interface ChefMenuItem {
  id: string;
  name: string;
  category_id: string;
  price: number;
  description: string;
  kitchen_station: string;
  is_available: boolean;
}

export interface ChefMenuCategory {
  id: string;
  name: string;
}

export interface CreateChefOrderPayload {
  venueId: string;
  tableNumber: string;
  serviceMode: 'dine-in' | 'takeaway';
  items: Array<{
    itemId: string;
    name: string;
    price: number;
    quantity: number;
    notes?: string | null;
    kitchenStation?: string;
  }>;
  chefName?: string;
}

// In-memory cache for chef menu categories and items (5-minute TTL)
interface CachedChefMenu {
  data: { categories: ChefMenuCategory[]; items: ChefMenuItem[] };
  timestamp: number;
}
const chefMenuCache = new Map<string, CachedChefMenu>();
const CHEF_MENU_CACHE_TTL_MS = 5 * 60 * 1000;

export const invalidateChefMenuCache = (venueId?: string): void => {
  if (venueId) {
    chefMenuCache.delete(venueId);
  } else {
    chefMenuCache.clear();
  }
};

/**
 * Fetches available menu categories and items for the venue with TTL caching.
 */
export const fetchVenueMenuForChef = async (
  venueId: string
): Promise<{ categories: ChefMenuCategory[]; items: ChefMenuItem[] }> => {
  const cached = chefMenuCache.get(venueId);
  if (cached && Date.now() - cached.timestamp < CHEF_MENU_CACHE_TTL_MS) {
    return cached.data;
  }

  const [categoriesRes, itemsRes] = await Promise.all([
    supabase
      .from('categories')
      .select('id, name')
      .eq('venue_id', venueId)
      .order('order', { ascending: true }),
    supabase
      .from('items')
      .select('id, name, category_id, price, description, is_available, kitchen_station')
      .eq('venue_id', venueId)
      .eq('is_available', true)
      .order('name'),
  ]);

  if (categoriesRes.error) throw categoriesRes.error;
  if (itemsRes.error) throw itemsRes.error;

  const categories: ChefMenuCategory[] = categoriesRes.data || [];
  const items: ChefMenuItem[] = (itemsRes.data || []).map((i: any) => ({
    id: i.id,
    name: i.name,
    category_id: i.category_id,
    price: Number(i.price) || 0,
    description: i.description || '',
    kitchen_station: i.kitchen_station || 'hot',
    is_available: i.is_available,
  }));

  const result = { categories, items };
  chefMenuCache.set(venueId, { data: result, timestamp: Date.now() });
  return result;
};

/**
 * Dispatches a new direct order directly into the kitchen queue.
 */
export const createDirectChefOrder = async (
  payload: CreateChefOrderPayload
): Promise<string> => {
  if (payload.items.length === 0) {
    throw new Error('At least one item is required to create an order.');
  }

  const subtotal = payload.items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // 1. Create order record with preparing status and confirmed flag
  const { data: newOrder, error: orderErr } = await supabase
    .from('orders')
    .insert({
      venue_id: payload.venueId,
      status: 'preparing',
      priority: 'normal',
      total_amount: subtotal,
      type: payload.serviceMode,
      metadata: {
        table_number: payload.tableNumber,
        waiter_name: payload.chefName || 'Chef Station',
        service_mode: payload.serviceMode,
        confirmed_to_kitchen: true,
        confirmed_to_kitchen_at: new Date().toISOString(),
      },
    })
    .select('id')
    .single();

  if (orderErr) {
    console.error('[chefOrderCreation.service] Order insert failed:', orderErr);
    throw orderErr;
  }

  // 2. Insert order items
  const orderItemsRows = payload.items.map(item => ({
    order_id: newOrder.id,
    item_id: item.itemId,
    price: item.price,
    quantity: item.quantity,
    notes: item.notes || null,
    kitchen_station: item.kitchenStation || 'hot',
    status: 'sent_to_kitchen',
    sent_to_kitchen_at: new Date().toISOString(),
  }));

  const { error: itemsErr } = await supabase
    .from('order_items')
    .insert(orderItemsRows);

  if (itemsErr) {
    console.error('[chefOrderCreation.service] Order items insert failed:', itemsErr);
    throw itemsErr;
  }

  return newOrder.id;
};
